
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td, Button } from '../components/UI';
import { inventoryData } from '../data/inventory';
import { StockStatus, InventoryItem } from '../types';
import { useTranslation } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { ArrowLeft, RefreshCw, AlertCircle, ShoppingCart } from '../components/Icons';

const ReplenishmentPlanning: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addItem, totalItems } = useCart();
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  // Filter only critical items, then apply column filters
  const filteredItems = useMemo(() => {
    let items = inventoryData.filter(item => item.status === StockStatus.CRITICAL);
    
    return items.filter(item => {
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = String((item as any)[key] || '').toLowerCase();
        return itemValue.includes((value as string).toLowerCase());
      });
    });
  }, [columnFilters]);

  const handleReplenish = (item: any) => {
    const replenishQty = (item.minStock * 2) - item.currentStock;
    const price = 25.00; 
    addItem(item, replenishQty, price);
  };

  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('replenishment')} 
        breadcrumbs={[{ label: 'VMI Hub' }, { label: t('replenishment') }]}
        actions={
          <Button onClick={() => navigate('/request-cart')} className="relative">
            <ShoppingCart size={16} className="mr-2" /> REQUEST CART
            {totalItems > 0 && (
              <span className="ml-2 bg-white text-brand-dark px-1.5 py-0.5 rounded-full text-[10px] font-black">
                {totalItems}
              </span>
            )}
          </Button>
        }
      />

      <Card className="border-l-4 border-l-red-500 p-6 bg-red-50/30 dark:bg-red-900/10 mb-6">
        <div className="flex items-start gap-4">
           <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full text-red-600 dark:text-red-400">
              <AlertCircle size={24} />
           </div>
           <div>
              <h3 className="text-lg font-bold text-red-700 dark:text-red-400">Action Required</h3>
              <p className="text-sm text-red-600/80 dark:text-red-400/80 mt-1">
                 {filteredItems.length} items are below minimum stock levels and require immediate consignment.
              </p>
           </div>
        </div>
      </Card>

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
        <Table>
          <Thead>
            <Tr isHeader>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_number')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('itemNumber', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_name')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('itemName', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('supplier')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('supplier', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-center">
                 <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('current_stock')}</span>
              </Th>
              <Th className="text-center">
                 <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('min_stock')}</span>
              </Th>
              <Th className="text-center">
                 <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Deficit</span>
              </Th>
              <Th className="text-center">
                 <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('actions')}</span>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredItems.length > 0 ? filteredItems.map(item => (
              <Tr key={item.id}>
                <Td><span className="font-bold font-mono text-brand-dark dark:text-white">{item.itemNumber}</span></Td>
                <Td>{item.itemName}</Td>
                <Td className="text-gray-500">{item.supplier}</Td>
                <Td className="text-center font-bold text-red-600">{item.currentStock}</Td>
                <Td className="text-center">{item.minStock}</Td>
                <Td className="text-center font-bold text-red-600">-{item.minStock - item.currentStock}</Td>
                <Td className="text-center">
                   <div className="flex justify-center">
                     <Button size="sm" variant="outline" className="shadow-sm flex items-center gap-2" onClick={() => handleReplenish(item)}>
                        <ShoppingCart size={14} /> ADD TO REQUEST
                     </Button>
                   </div>
                </Td>
              </Tr>
            )) : (
              <Tr>
                <Td colSpan={7} className="text-center py-12 text-gray-400 font-bold uppercase tracking-widest">
                   No critical items found matching your filters.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
};

export default ReplenishmentPlanning;
